// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect } from 'react';

import { OnChangeFirstStepForm } from '../';
import { useOrderState } from '../../../../context';

import { MAX_LENGTH_SHIFT_SUPERVISOR_INSTRUCTIONS } from '@/constants/text';
import ItemSelectField from '@/form/ItemSelectField';
import TextAreaField from '@/form/TextAreaField';
import TextField from '@/form/TextField';
import MaskedInputField from '@/form/MaskedInputField';
import OptionField from '@/form/OptionField';
import Stack from '@/components/Stack';
import { axiosRubyClient } from '@/util/axios/axiosClient';

import styled from '@/styles';
import { CreateJobContactAdminType } from '@/types';
import useMediaQuery from '@/hooks/useMediaQuery';

type Props = {
  onChange: OnChangeFirstStepForm;
};

// Create a special type for "Other" option
type ContactOption =
  | CreateJobContactAdminType
  | {
      id: 'other';
      user: { id: 'other'; firstName: 'Other'; lastName: 'Shift Supervisor' };
    };

// Type for custom supervisor data
type CustomSupervisor = {
  name: string;
  phone: string;
  isComplete: boolean;
};

const CreateContactFormWrapper = styled('div', {
  width: '100%',
});

// Store custom supervisor data globally so it can be accessed by the submit handler
let customSupervisorData: CustomSupervisor | null = null;

// Function to create custom shift supervisor via API
export const createCustomShiftSupervisor = async (
  customer_id: string,
): Promise<CreateJobContactAdminType | null> => {
  if (!customSupervisorData || !customSupervisorData.isComplete) {
    return null;
  }

  try {
    const response = await axiosRubyClient.post('/api/shift_supervisors', {
      name: customSupervisorData.name,
      contact: customSupervisorData.phone,
      customer_id: customer_id,
    });

    const { shift_supervisor } = response.data;

    // Convert the API response to the expected format
    const customContact: CreateJobContactAdminType = {
      id: shift_supervisor.id.toString(),
      user: {
        id: shift_supervisor.id.toString(),
        firstName: customSupervisorData.name,
        lastName: '',
      },
    };

    // Clear the custom data after successful creation
    customSupervisorData = null;

    return customContact;
  } catch (error) {
    console.error('Failed to create custom shift supervisor:', error);
    throw error;
  }
};

// Function to check if we have a pending custom supervisor
export const hasPendingCustomSupervisor = (): boolean => {
  return customSupervisorData !== null && customSupervisorData.isComplete;
};

const CreateContactForm = ({ onChange }: Props) => {
  const { billing } = useOrderState();
  const admins = billing?.customer.admins.items || [];
  const [showOtherForm, setShowOtherForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customPhone, setCustomPhone] = useState('');
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [showVerificationEnabled, setShowVerificationEnabled] = useState(false);
  const [verificationEnabledDefault, setVerificationEnabledDefault] =
    useState(false);
  const [isVerificationDataLoaded, setIsVerificationDataLoaded] =
    useState(false);

  // Fetch verification_enabled from Ruby API on mount
  useEffect(() => {
    const fetchVerificationEnabled = async () => {
      if (!billing?.customer?.id) {
        setIsVerificationDataLoaded(true);
        return;
      }

      try {
        const response = await axiosRubyClient.get(
          `/api/customers/${billing.customer.id}`,
        );

        console.log('GET /api/customers response:', response.data);

        const verificationEnabled = response.data?.verification_enabled;

        // Set both the visibility flag and the default value
        setShowVerificationEnabled(verificationEnabled === true);
        setVerificationEnabledDefault(verificationEnabled === true);

        // Set the form value
        onChange(
          { value: verificationEnabled === true },
          'verification_enabled',
        );
      } catch (error: any) {
        console.error('Failed to fetch verification_enabled:', error);
        // On error, hide the field
        setShowVerificationEnabled(false);
        setVerificationEnabledDefault(false);
      } finally {
        // Mark data as loaded after API call completes
        setIsVerificationDataLoaded(true);
      }
    };

    fetchVerificationEnabled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billing?.customer?.id]);

  // Create the options list with "Other" option at the end only for web
  const contactOptions: ContactOption[] = [
    ...admins,
    ...(!phoneOnly
      ? [
          {
            id: 'other',
            user: {
              id: 'other',
              firstName: 'Other',
              lastName: 'Shift Supervisor',
            },
          },
        ]
      : []),
  ];

  const updateCustomSupervisorData = (name: string, phone: string) => {
    const hasValidName = name.trim().length > 0;
    const hasValidPhone = phone.length >= 14; // (000) 000-0000 format
    const isComplete = hasValidName && hasValidPhone;

    customSupervisorData = {
      name: name.trim(),
      phone: phone,
      isComplete,
    };

    // If both name and phone are filled, create a temporary contact for validation
    if (isComplete) {
      const tempContact: CreateJobContactAdminType = {
        id: 'custom_pending', // Temporary ID
        user: {
          id: 'custom_pending',
          firstName: customName.trim(),
          lastName: '',
        },
      };
      onChange({ value: tempContact }, 'contact');
    } else {
      // If incomplete, set contact to null to trigger validation
      onChange({ value: null }, 'contact');
    }
  };

  const handleContactChange = (fieldContext: any) => {
    const contact = fieldContext.value as ContactOption;

    if (contact?.id === 'other') {
      setShowOtherForm(true);
      // Clear any previous custom data
      customSupervisorData = null;
      // Don't set contact yet, wait for custom form completion
      onChange({ value: null }, 'contact');
    } else {
      setShowOtherForm(false);
      setCustomName('');
      setCustomPhone('');
      customSupervisorData = null;
      // Pass the selected admin contact
      onChange(fieldContext, 'contact');
    }
  };

  const handleOtherNameChange = (fieldContext: any) => {
    const name = fieldContext.value;
    setCustomName(name);
    updateCustomSupervisorData(name, customPhone);
  };

  const handleOtherPhoneChange = (fieldContext: any) => {
    const phone = fieldContext.value;
    setCustomPhone(phone);
    updateCustomSupervisorData(customName, phone);
  };

  return (
    <CreateContactFormWrapper>
      {isVerificationDataLoaded && showVerificationEnabled && (
        <>
          <OptionField
            appearance="checkbox"
            callback={(fieldContext) => {
              onChange(fieldContext, 'verification_enabled');
            }}
            defaultValue={verificationEnabledDefault}
            fieldId="verification_enabled"
            label="Enable QR Code Check-in/Check-out"
          />

          <div
            style={{
              marginLeft: '32px',
              marginTop: '-20px',
              marginBottom: '16px',
            }}
          >
            <span style={{ fontSize: '14px', color: '#666' }}>
              When enabled, workers will use QR code scanning to verify their
              check-in and check-out times on-site, ensuring accurate attendance
              tracking.
            </span>
          </div>
        </>
      )}

      <ItemSelectField<ContactOption>
        required
        callback={handleContactChange}
        fieldId="contact"
        itemToKey={(item) => item.id}
        itemToString={(item) => {
          if (!item) return '';
          if (item.id === 'other') {
            return '+ Other Shift Supervisor';
          }
          return `${item.user.firstName} ${item.user.lastName}`;
        }}
        items={contactOptions}
        label="Shift Supervisor"
        placeholder="Select a Shift Supervisor..."
      />

      {showOtherForm && (
        <Stack vertical gap={0} css={{ marginTop: 16 }}>
          <TextField
            required
            autoFocus
            fieldId="otherSupervisorName"
            placeholder="Enter Shift Supervisor name"
            callback={handleOtherNameChange}
          />
          <MaskedInputField
            required
            fieldId="otherSupervisorPhone"
            label="Contact Number"
            mask="(000) 000-0000"
            placeholder="(555) 555-5555"
            type="tel"
            incompletemessage="Must be a valid phone number"
            callback={handleOtherPhoneChange}
          />
        </Stack>
      )}

      <OptionField
        appearance="checkbox"
        callback={(fieldContext) => {
          onChange(fieldContext, 'shareContactNumber');
        }}
        fieldId="shareContactNumber"
        label="Share contact number with GravyWorkers"
      />

      <div
        style={{ marginLeft: '32px', marginTop: '-20px', marginBottom: '16px' }}
      >
        <span style={{ fontSize: '14px', color: '#666' }}>
          This will be shared with hired GravyWorkers{' '}
          <strong>90 minutes</strong> before the shift starts.
        </span>
      </div>

      <TextAreaField
        callback={(fieldContext) => {
          onChange(fieldContext, 'contactInstructions');
        }}
        fieldId="contactInstructions"
        label={
          <>
            <strong>Check-in Instructions (Optional)</strong>
            <br />
            <br />
            These optional instructions will be shared with hired workers{' '}
            <strong>90 minutes</strong> before the shift starts to help them check
            in with the shift supervisor at the right time.
          </>
        }
        maxLength={MAX_LENGTH_SHIFT_SUPERVISOR_INSTRUCTIONS}
        placeholder="Provide instructions for how workers should check-in with you on the worksite. For example, checkin with your shift supervisor as the hotel lobby. "
      />
    </CreateContactFormWrapper>
  );
};

export default CreateContactForm;
